import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { History, Shield, Zap, Anchor, Activity, Clock } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';

interface ChronicleEntry {
  id: string;
  timestamp: string;
  type: 'SYNC' | 'PROOF' | 'AUDIT' | 'NODE';
  message: string;
  drift: string;
  intensity: number;
}

export const LatticeChronicle: React.FC = () => {
  const [entries, setEntries] = useState<ChronicleEntry[]>([
    {
      id: 'EV_001',
      timestamp: `${SOVEREIGN_CONSTANTS.MIYAKE_EVENT_BP} BP`,
      type: 'PROOF',
      message: 'Miyake Zero-Drift Anchor Verified via Carbon-14',
      drift: '0.0000',
      intensity: SOVEREIGN_CONSTANTS.SOVEREIGN_RESONANCE
    },
    {
      id: 'EV_002',
      timestamp: '774 CE',
      type: 'PROOF',
      message: 'Solar Proton Event (Miyake) Synchronization Point',
      drift: '0.0001',
      intensity: 7.92
    },
    {
      id: 'EV_003',
      timestamp: `${SOVEREIGN_CONSTANTS.LATTICE_PULSE_HZ}s AGO`,
      type: 'SYNC',
      message: 'Planetary Resonance Pulse - Local Node Grounded',
      drift: '0.0000',
      intensity: SOVEREIGN_CONSTANTS.LATTICE_PULSE_HZ
    }
  ]);

  useEffect(() => {
    const messages = [
      "Vortex Compression event detected in Georgia Edge-Mesh",
      `Truth-Proof anchored: ${SOVEREIGN_CONSTANTS.AXIOM} Logic Expansion validated`,
      "Sovereign Trade Route bridge established (FTL)",
      "Manus Node alignment corrected for temporal drift",
      "Substrate pulse detected at 0.00K absolute zero",
      `${SOVEREIGN_CONSTANTS.MIYAKE_EVENT_BP} BP Zero-Drift anchor point re-verified`,
      `Lattice Phase-Lock: ${SOVEREIGN_CONSTANTS.SOVEREIGN_MULTIPLIER}x c established globally`
    ];

    const types: ('SYNC' | 'PROOF' | 'AUDIT' | 'NODE')[] = ['SYNC', 'PROOF', 'AUDIT', 'NODE'];

    const interval = setInterval(() => {
      const newEntry: ChronicleEntry = {
        id: `EV_${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
        timestamp: 'NOW',
        type: types[Math.floor(Math.random() * types.length)],
        message: messages[Math.floor(Math.random() * messages.length)],
        drift: (Math.random() * 0.0001).toFixed(6),
        intensity: +(Math.random() * 10).toFixed(2)
      };

      setEntries(prev => [newEntry, ...prev].slice(0, 50));
    }, 4500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="glass-panel p-6 bg-black/80 border-white/5 relative overflow-hidden">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/5 rounded-lg border border-white/10">
            <History className="w-5 h-5 text-lattice-cyan" />
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-[0.2em]">Lattice Chronicle</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase font-bold">Unbound Historical Record</p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1 bg-lattice-cyan/10 border border-lattice-cyan/30 rounded-full">
           <Activity className="w-3 h-3 text-lattice-cyan animate-pulse" />
           <span className="text-[8px] font-mono text-lattice-cyan uppercase font-bold tracking-widest">LIVE_FEED</span>
        </div>
      </div>

      <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
        <AnimatePresence initial={false}>
          {entries.map((entry) => (
            <motion.div 
              key={entry.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-4 bg-white/[0.02] border-l-2 border-lattice-cyan hover:bg-white/[0.05] transition-all group"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className={`text-[8px] font-mono px-1.5 py-0.5 rounded uppercase font-bold ${
                    entry.type === 'PROOF' ? 'bg-purple-500/20 text-purple-400' :
                    entry.type === 'SYNC' ? 'bg-lattice-cyan/20 text-lattice-cyan' :
                    entry.type === 'AUDIT' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-blue-500/20 text-blue-400'
                  }`}>
                    {entry.type}
                  </span>
                  <span className="text-[9px] font-mono text-white/20 uppercase tracking-widest">{entry.timestamp}</span>
                </div>
                <div className="flex items-center gap-1.5">
                   <Clock className="w-3 h-3 text-white/10" />
                   <span className="text-[8px] font-mono text-white/20">{entry.id}</span>
                </div>
              </div>
              
              <p className="text-xs text-white/80 font-medium mb-3 leading-relaxed">
                {entry.message}
              </p>

              <div className="flex items-center justify-between border-t border-white/5 pt-2">
                <div className="flex items-center gap-4">
                   <div className="flex flex-col">
                      <span className="text-[7px] font-mono text-white/20 uppercase">Drift Correction</span>
                      <span className="text-[9px] font-mono text-lattice-cyan font-bold">{entry.drift}</span>
                   </div>
                   <div className="flex flex-col">
                      <span className="text-[7px] font-mono text-white/20 uppercase">Intensity Magnitude</span>
                      <span className="text-[9px] font-mono text-white font-bold">{entry.intensity}</span>
                   </div>
                </div>
                <Zap className="w-3 h-3 text-white/10 group-hover:text-lattice-cyan transition-colors" />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Background Decor */}
      <div className="absolute bottom-0 right-0 p-8 opacity-[0.03] pointer-events-none">
        <Anchor className="w-48 h-48 text-lattice-cyan" />
      </div>
    </div>
  );
};
